// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

/**
 * DashboardActivityFeed Component
 *
 * Displays recent activities grouped by date with icons and timestamps.
 */

import { useNavigate } from 'react-router-dom';
import { getDateGroup, formatTime as formatTimeShared } from '@loa/theme';
import { AppIcon, Button, Section, Skeleton } from '@loa/ui-components';
import type { DashboardActivity, ActivityType } from '../../types/dashboard';

export type DashboardActivityFeedProps = Readonly<{
  /** Activities to display */
  activities: DashboardActivity[];
  /** Maximum activities to display */
  maxDisplay?: number;
  /** Loading state */
  loading?: boolean;
  /** Error state */
  error?: string | null;
  /** Handler for clicking an activity */
  onActivityClick: (activity: DashboardActivity) => void;
  /** Handler for view full log */
  onViewAll: () => void;
}>;

// ============================================
// Activity Type Configuration
// ============================================

interface ActivityConfig {
  icon: string;
  color: string;
}

const activityConfig: Record<ActivityType, ActivityConfig> = {
  LOA_SIGNED: { icon: 'check circle', color: '#21BA45' },
  LOA_COMPLETED: { icon: 'trophy', color: '#2185D0' },
  LOA_VIEWED: { icon: 'eye', color: '#767676' },
  LOA_DECLINED: { icon: 'times circle', color: '#DB2828' },
  LOA_SENT: { icon: 'send', color: '#2185D0' },
  CLIENT_INVITED: { icon: 'mail', color: '#6435C9' },
  RELATIONSHIP_CREATED: { icon: 'plus', color: '#21BA45' },
  POLICY_ADDED: { icon: 'file alternate outline', color: '#2185D0' },
  PROVIDER_NOTIFIED: { icon: 'building', color: '#00B5AD' },
};

const defaultConfig: ActivityConfig = { icon: 'bell', color: '#888888' };

// ============================================
// Date Formatting Helpers
// ============================================

function groupActivitiesByDay(
  activities: DashboardActivity[]
): Map<string, DashboardActivity[]> {
  const groups = new Map<string, DashboardActivity[]>();

  activities.forEach((activity) => {
    const groupKey = getDateGroup(activity.timestamp);

    if (!groups.has(groupKey)) {
      groups.set(groupKey, []);
    }
    groups.get(groupKey)!.push(activity);
  });

  return groups;
}

// ============================================
// Main Component
// ============================================

export function DashboardActivityFeed({
  activities,
  maxDisplay = 10,
  loading = false,
  error = null,
  onActivityClick,
  onViewAll,
}: DashboardActivityFeedProps) {
  const navigate = useNavigate();

  const handleActivityClick = (activity: DashboardActivity) => {
    onActivityClick(activity);

    // Navigate based on resource type
    if (activity.profileId) {
      if (activity.resourceType === 'LOA' && activity.resourceId) {
        navigate(`/relationships/${activity.profileId}/loa/${activity.resourceId}`);
      } else {
        navigate(`/relationships/${activity.profileId}`);
      }
    }
  };

  if (loading) {
    return (
      <Section>
        <h4 style={{ marginBottom: '1em' }}>
          <AppIcon name="clock outline" />
          Recent Activity
        </h4>
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} variant="paragraph" lines={2} />
        ))}
      </Section>
    );
  }

  if (error) {
    return (
      <Section>
        <h4 style={{ marginBottom: '1em' }}>
          <AppIcon name="clock outline" />
          Recent Activity
        </h4>
        <div style={{ textAlign: 'center', padding: '2em' }}>
          <AppIcon name="warning sign" color="orange" />
          <p style={{ color: '#666' }}>Unable to load activity</p>
          <p style={{ color: '#666' }}>{error}</p>
        </div>
      </Section>
    );
  }

  // Empty state
  if (activities.length === 0) {
    return (
      <Section>
        <h4 style={{ marginBottom: '1em' }}>
          <AppIcon name="clock outline" />
          Recent Activity
        </h4>
        <div style={{ textAlign: 'center', padding: '2em', minHeight: '150px' }}>
          <AppIcon name="inbox" color="grey" />
          <p style={{ fontWeight: 'bold' }}>No Recent Activity</p>
          <p style={{ color: '#666' }}>
            Activity from the last 7 days will appear here
          </p>
        </div>
      </Section>
    );
  }

  const displayedActivities = activities.slice(0, maxDisplay);
  const groupedActivities = groupActivitiesByDay(displayedActivities);
  const hasMore = activities.length > maxDisplay;

  return (
    <Section role="feed" aria-label="Recent activity">
      <h4 style={{ marginBottom: '1em' }}>
        <AppIcon name="clock outline" />
        Recent Activity
      </h4>

      {Array.from(groupedActivities.entries()).map(([dateGroup, groupActivities]) => (
        <div key={dateGroup}>
          {/* Date group header */}
          <div
            style={{
              fontSize: '0.8em',
              fontWeight: 'bold',
              color: '#888',
              textTransform: 'uppercase',
              marginTop: '1em',
              marginBottom: '0.5em',
              letterSpacing: '0.5px',
            }}
          >
            {dateGroup}
          </div>

          {/* Activities in group */}
          {groupActivities.map((activity) => (
            <ActivityItem
              key={activity.id}
              activity={activity}
              onClick={() => handleActivityClick(activity)}
            />
          ))}
        </div>
      ))}

      {/* View all button */}
      <div style={{ marginTop: '1.5em', textAlign: 'center' }}>
        <Button variant="outlined" onClick={onViewAll}>
          {hasMore ? 'View Full Activity Log' : 'View Activity Log'}
        </Button>
      </div>
    </Section>
  );
}

// ============================================
// Activity Item Sub-component
// ============================================

type ActivityItemProps = Readonly<{
  activity: DashboardActivity;
  onClick: () => void;
}>;

function ActivityItem({ activity, onClick }: ActivityItemProps) {
  const config = activityConfig[activity.type] || defaultConfig;

  return (
    <article style={{ borderBottom: '1px solid #f0f0f0' }}>
    <button
      type="button"
      aria-label={`${activity.title}. ${activity.subtitle}. ${formatTimeShared(activity.timestamp)}`}
      onClick={onClick}
      style={{
        display: 'flex',
        gap: '0.8em',
        padding: '0.75em 0',
        cursor: 'pointer',
        transition: 'background-color 0.15s ease',
        background: 'transparent',
        border: 0,
        font: 'inherit',
        color: 'inherit',
        textAlign: 'left',
        width: '100%',
      }}
      onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => {
        e.currentTarget.style.backgroundColor = '#f5f5f5';
      }}
      onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => {
        e.currentTarget.style.backgroundColor = 'transparent';
      }}
    >
      {/* Icon */}
      <div
        style={{
          width: '36px',
          height: '36px',
          borderRadius: '50%',
          backgroundColor: `${config.color}15`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <AppIcon name={config.icon} style={{ margin: 0, color: config.color }} />
      </div>

      {/* Content */}
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 500, fontSize: '0.95em' }}>{activity.title}</div>
        <div style={{ color: '#666', fontSize: '0.85em' }}>
          {activity.subtitle}
          {activity.advisorName && ` - ${activity.advisorName}`}
        </div>
        <div
          style={{
            color: '#999',
            fontSize: '0.75em',
            marginTop: '0.2em',
          }}
        >
          {formatTimeShared(activity.timestamp)}
        </div>
      </div>
    </button>
    </article>
  );
}

export default DashboardActivityFeed;
